import { useState } from "react";
import { Link } from "react-router-dom";
import { Wand2, ArrowRight, Cable, RadioTower, Signal, Gift, CalendarCheck, Wrench, Smartphone, LayoutDashboard } from "lucide-react";
import { money } from "../../lib/api";
import { PackageCard } from "../PackageCard";

const minOf = (pkgs, t) => {
  const v = pkgs.filter((p) => p.type === t).map((p) => p.retail_price);
  return v.length ? Math.min(...v) : null;
};

export const Deals = ({ pkgs, country }) => {
  const ccy = country?.currency;
  const deals = [
    { to: "/internet/fibre", g: "from-[#F0326E] to-[#FF7A45]", icon: Cable, k: "Fibre", t: minOf(pkgs, "fibre") != null ? `Uncapped fibre from ${money(minOf(pkgs, "fibre"), ccy)}/mo` : "Fibre coming soon" },
    { to: "/domains", g: "from-[#0FB5A5] to-[#22C55E]", icon: Gift, k: "Free domain", t: `Free ${country?.free_tld || ".africa"} domain with any package` },
    { to: "/internet/5g", g: "from-[#FF9F1C] to-[#FFC23D]", icon: RadioTower, k: "Fixed 5G", t: minOf(pkgs, "5g") != null ? `Plug-in 5G from ${money(minOf(pkgs, "5g"), ccy)}/mo` : "5G coming soon" },
    { to: "/website-builder", g: "from-[#1B1033] to-[#F0326E]", icon: Wand2, k: "Free website", t: "Type or speak, and AI builds your free website" },
    { to: "/internet/lte", g: "from-[#6D28D9] to-[#F0326E]", icon: Signal, k: "LTE", t: minOf(pkgs, "lte") != null ? `LTE data from ${money(minOf(pkgs, "lte"), ccy)}/mo` : "LTE coming soon" },
  ];
  return (
    <div className="no-scrollbar -mx-4 flex snap-x gap-4 overflow-x-auto px-4 pb-2" data-testid="deals-carousel">
      {deals.map(({ to, g, icon: Icon, k, t }, i) => (
        <Link key={k} to={to} data-testid={`deal-card-${k.toLowerCase().replace(/\s+/g, "-")}`} style={{ animationDelay: `${i * 80}ms` }}
          className={`fade-up group relative flex min-h-[170px] w-[270px] shrink-0 snap-start flex-col justify-between overflow-hidden rounded-3xl bg-gradient-to-br ${g} p-6 text-white transition-transform duration-300 hover:-translate-y-1 hover:rotate-[-1deg] sm:w-[300px]`}>
          <Icon className="absolute -right-4 -top-4 h-28 w-28 opacity-15 transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110" />
          <span className="w-fit rounded-full bg-white/25 px-3 py-1 text-[11px] font-bold uppercase tracking-wider backdrop-blur">{k}</span>
          <p className="font-display text-xl font-extrabold leading-tight">{t}</p>
          <span className="flex items-center gap-1 text-sm font-bold">Get this deal <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
        </Link>
      ))}
    </div>
  );
};

export const NetworkMarquee = ({ pkgs }) => {
  const nets = [...new Set(pkgs.map((p) => p.network))];
  if (!nets.length) return null;
  const row = [...nets, ...nets, ...nets].slice(0, Math.max(10, nets.length));
  return (
    <div className="relative overflow-hidden py-2" data-testid="network-marquee">
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-24 bg-gradient-to-r from-[#FEF6F8] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-24 bg-gradient-to-l from-[#FEF6F8] to-transparent" />
      <div className="marquee-track gap-4">
        {[...row, ...row].map((n, i) => (
          <span key={i} className="whitespace-nowrap rounded-full border bg-white px-6 py-3 font-display text-lg font-bold text-foreground/60 transition-colors hover:border-primary hover:text-primary">{n}</span>
        ))}
      </div>
    </div>
  );
};

const TABS = [["fibre", "Fibre", Cable], ["5g", "Fixed 5G", RadioTower], ["lte", "LTE", Signal]];

export const ProductTabs = ({ pkgs }) => {
  const [t, setT] = useState("fibre");
  const list = pkgs.filter((p) => p.type === t).slice(0, 3);
  return (
    <div data-testid="featured-packages">
      <div className="flex flex-wrap items-center gap-2">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setT(k)} data-testid={`tech-tile-${k}`}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-[background-color,color,transform] duration-200 ${t === k ? "bg-primary text-white shadow-[0_10px_24px_-10px_rgba(240,50,110,0.8)]" : "bg-muted text-foreground/70 hover:-translate-y-0.5 hover:text-primary"}`}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
        <Link to={`/internet/${t}`} className="ml-auto flex items-center gap-1 text-sm font-bold text-primary" data-testid="view-all-packages-link">
          View all <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div key={t} className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {list.length ? list.map((p, i) => <PackageCard key={p.id} pkg={p} delay={i * 70} highlight={i === 1} />)
          : <p className="text-sm text-muted-foreground">No packages of this type yet.</p>}
      </div>
    </div>
  );
};

const WHY = [
  [CalendarCheck, "No fixed-term contracts", "Every package is month-to-month. Change or cancel whenever you like.", "bg-[#FFE4EA] text-primary"],
  [Wrench, "Free setup", "We coordinate installation with the local network. You just pick a time.", "bg-[#DDF7EE] text-secondary"],
  [Smartphone, "Pay your way", "Card, mobile money, bank transfer or USSD, all in your local currency.", "bg-[#FFF3D6] text-[#C27C00]"],
  [LayoutDashboard, "You're in control", "Track installation, pay invoices, manage domains and get help in one portal.", "bg-[#E4F2FC] text-[#1D6FB8]"],
];

export const WhyGrid = () => (
  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="why-grid">
    {WHY.map(([Icon, t, d, tone], i) => (
      <div key={t} style={{ animationDelay: `${i * 80}ms` }}
        className="fade-up group rounded-3xl border bg-white p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(27,16,51,0.25)]">
        <span className={`grid h-12 w-12 place-items-center rounded-2xl ${tone} transition-transform duration-300 group-hover:rotate-[-8deg] group-hover:scale-110`}><Icon className="h-6 w-6" /></span>
        <h3 className="mt-5 text-lg font-bold">{t}</h3>
        <p className="mt-1.5 text-sm text-muted-foreground">{d}</p>
      </div>
    ))}
  </div>
);
